import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import scrollToElement from '../../../helpers/scroll-to';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import { AddressDeliveryType } from '../../../models/address-delivery-type';
import { CountryType } from '../../../models/country-type';
import { DeliveryAddressesType } from '../../../models/delivery-addresses-type';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import accountCountries from '../../../services/customer-api/endpoints/account/countries';
import addressCustomer from '../../../services/customer-api/endpoints/address/customer';
import addressDelete from '../../../services/customer-api/endpoints/address/delete';
import LoadingSpinner from '../../atoms/loading-spinner/loading-spinner';
import AlertModal from '../../molecules/alert-modal/alert-modal';
import DeliveryAddressCard from '../../molecules/delivery-address-card/delivery-address-card';
import DeliveryAddressesTop from '../../molecules/delivery-addresses-top/delivery-addresses-top';
import ItemPagination from '../../molecules/item-pagination/item-pagination';
import NoDataBlock from '../../molecules/no-data-block/no-data-block';
import UpdateAddressForm from '../../molecules/update-address-form/update-address-form';
import open from '../../providers/overlay/open.overlay.actions';
import { OverlayDispatchContext } from '../../providers/overlay/overlay.provider';
import useGetSessionId from '../../providers/user/hooks/use-get-session-id.hook';
import useDebounce from '../../../hooks/use-debounce/use-debounce';

const DeliveryAddresses: FC<DeliveryAddressesType> = (
  props: DeliveryAddressesType
) => {
  const {
    deleteAddressDescription,
    deleteAddressHeading,
    deleteAddressNoButtonText,
    deleteAddressYesButtonText,
    noAddressesDescription,
    noAddressesHeading,
  } = props;
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const overlayDispatch = useContext(OverlayDispatchContext);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [addresses, setAddresses] = useState<AddressDeliveryType[]>();
  const [countries, setCountries] = useState<CountryType[]>();
  const [editingAddress, setEditingAddress] = useState<AddressDeliveryType>();
  const [filterSearchTerm, setFilterSearchTerm] = useState<string>(null);
  const debouncedFilterSearchTerm = useDebounce(filterSearchTerm);
  const [page, setPage] = useState<number>(0);
  const [showAddressForm, setShowAddressForm] = useState<boolean>(false);
  const getSessionId = useGetSessionId();
  const itemsPerPage = 12;
  const addressesContainer = useRef(null);

  const getAddresses = (): void => {
    getSessionId().then((sessionId) =>
      addressCustomer(sessionId).then((response: AddressDeliveryType[]) => {
        setAddresses(response);
        setIsDeleting(false);
      })
    );
  };

  const getCountries = (): void => {
    accountCountries().then((response: CountryType[]) =>
      setCountries(response)
    );
  };

  const deleteAddress = async (address: AddressDeliveryType): Promise<void> => {
    setIsDeleting(true);
    await addressDelete(address.id, await getSessionId());
    getAddresses();
  };

  const handleShowAddressForm = (
    show: boolean,
    address?: AddressDeliveryType
  ) => {
    window.scrollTo({ top: 0 });
    setShowAddressForm(show);

    if (address) {
      setEditingAddress(address);

      return;
    }

    setEditingAddress(null);
  };

  const getFilteredData = (): AddressDeliveryType[] =>
    addresses?.filter(
      (address: AddressDeliveryType) =>
        !filterSearchTerm ||
        (address.addressLine1 &&
          address.addressLine1
            .toLowerCase()
            .includes(filterSearchTerm.toLowerCase())) ||
        (address.addressLine2 &&
          address.addressLine2
            .toLowerCase()
            .includes(filterSearchTerm.toLowerCase())) ||
        (address.addressLine3 &&
          address.addressLine3
            .toLowerCase()
            .includes(filterSearchTerm.toLowerCase())) ||
        (address.addressLine4 &&
          address.addressLine4
            .toLowerCase()
            .includes(filterSearchTerm.toLowerCase())) ||
        (address.addressLine5 &&
          address.addressLine5
            .toLowerCase()
            .includes(filterSearchTerm.toLowerCase())) ||
        (address.name &&
          address.name
            .toLowerCase()
            .includes(filterSearchTerm.toLowerCase())) ||
        (address.postcode &&
          address.postcode
            .toLowerCase()
            .includes(filterSearchTerm.toLowerCase()))
    );

  const getPaginatedData = (): AddressDeliveryType[] =>
    getFilteredData().slice(page * itemsPerPage, (page + 1) * itemsPerPage);

  useEffect(() => {
    getAddresses();
    getCountries();
  }, []);

  const openDeleteAlertModal = (address: AddressDeliveryType): void => {
    overlayDispatch(
      open(
        <AlertModal
          heading={deleteAddressHeading}
          message={deleteAddressDescription}
          cancelMessage={deleteAddressNoButtonText}
          onSubmit={() => {
            deleteAddress(address);

            pushToDataLayer(GtmEventNameEnum.AddressesDelete);
          }}
          submitMessage={deleteAddressYesButtonText}
        />
      )
    );
  };

  useEffect(() => {
    if (debouncedFilterSearchTerm?.length >= 3) {
      pushToDataLayer(GtmEventNameEnum.AddressesSearch);
    }
  }, [debouncedFilterSearchTerm]);

  return (
    <div className="py-component-spacing-mobile lg:py-component-spacing">
      {!showAddressForm && (
        <div ref={addressesContainer}>
          <DeliveryAddressesTop
            {...props}
            numberOfAddressesShown={getFilteredData()?.length}
            numberOfAddressesTotal={addresses?.length}
            onAdd={() => {
              handleShowAddressForm(true);

              pushToDataLayer(GtmEventNameEnum.AddressesStartNew);
            }}
            onSearch={(value: string) => setFilterSearchTerm(value)}
          />

          {(!addresses || !countries) && <LoadingSpinner className="h-96" />}

          <AnimatePresence>
            {addresses && !addresses.length && (
              <motion.div
                animate={{ opacity: 1 }}
                className="py-component-spacing-mobile lg:py-component-spacing"
                initial={{ opacity: 0 }}
              >
                <NoDataBlock
                  heading={noAddressesHeading}
                  message={noAddressesDescription}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {addresses?.length && countries?.length && (
              <>
                <motion.div
                  animate={{ opacity: 1 }}
                  className={`${
                    isDeleting ? 'animate-pulse filter grayscale' : ''
                  } gap-4 grid grid-cols-1 lg:grid-cols-2 mt-6 mb-12`}
                  initial={{ opacity: 0 }}
                >
                  {getPaginatedData().map((address: AddressDeliveryType) => (
                    <DeliveryAddressCard
                      key={address.id}
                      {...address}
                      {...props}
                      countries={countries}
                      onDelete={() => openDeleteAlertModal(address)}
                      onEdit={() => handleShowAddressForm(true, address)}
                    />
                  ))}
                </motion.div>

                <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
                  <ItemPagination
                    itemsPerPage={itemsPerPage}
                    totalItems={getFilteredData()?.length || 0}
                    onPageChange={(currentPage: number) => {
                      setPage(currentPage);
                      scrollToElement({
                        ref: addressesContainer.current,
                        offsetHeight: 20,
                        behavior: 'auto',
                      });
                    }}
                  />
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      )}

      <AnimatePresence>
        {showAddressForm && (
          <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
            <UpdateAddressForm
              {...props}
              address={editingAddress}
              countries={countries}
              onCancel={() => handleShowAddressForm(false)}
              onSubmit={() => {
                getAddresses();
                handleShowAddressForm(false);
                pushToDataLayer(
                  !editingAddress
                    ? GtmEventNameEnum.AddressesAdd
                    : GtmEventNameEnum.AddressesEdit
                );
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DeliveryAddresses;
